import { useMutation } from "@apollo/client/react";
import type { FragmentData } from "@gqlb/core";
import posthog from "posthog-js";
import { useCallback, useState } from "react";
import { b } from "@/b";
import { MEDIA_FRAGMENT } from "@/components/ResponsiveImage";
import { CartPreviewSection_Cart_Fragment } from "@/graphql/fragments";

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

/**
 * The mode the cart is being built for — resolved on the server (explicit
 * choice → last order → viewer location → default). The client never computes
 * a default itself; it only renders what the server decided.
 */
export const CartFulfillment_Fragment = b.fragment("CartFulfillment", "Cart", (b) => [
  b.fulfillment((b) => [b.mode(), b.source(), b.availableModes()]),
]);

export type CartFulfillment = FragmentData<typeof CartFulfillment_Fragment>["fulfillment"];
export type FulfillmentMode = CartFulfillment["mode"];
export type FulfillmentModeSource = CartFulfillment["source"];

export const SET_FULFILLMENT_MODE_MUTATION = b.mutation(
  "SetFulfillmentMode",
  { mode: "FulfillmentMode!", removeIneligible: "Boolean!", checkDates: "Boolean!" },
  (b, v) => [
    b.viewer((b) => [
      b.cart((b) => [
        b.setFulfillmentMode(
          { mode: v.mode, removeIneligible: v.removeIneligible, checkDates: v.checkDates },
          (b) => [
            b.applied(),
            b.cart((b) => [
              b.__fragment(CartFulfillment_Fragment),
              b.__fragment(CartPreviewSection_Cart_Fragment),
            ]),
            b.conflicts((b) => [
              b.itemId(),
              b.quantity(),
              b.reason(),
              b.product((b) => [b.id(), b.name(), b.images((b) => [b.__fragment(MEDIA_FRAGMENT)])]),
            ]),
          ],
        ),
      ]),
    ]),
  ],
);

export type FulfillmentConflictItem = {
  readonly itemId: string;
  readonly quantity: number;
  /** Set when the item's dates, not its mode exclusions, keep it from following. */
  readonly reason: string | null;
  readonly product: {
    readonly id: string;
    readonly name: string;
    readonly images: ReadonlyArray<FragmentData<typeof MEDIA_FRAGMENT>>;
  };
};

// ---------------------------------------------------------------------------
// Labels & copy
// ---------------------------------------------------------------------------

export const FULFILLMENT_MODE_ORDER: readonly FulfillmentMode[] = [
  "PICKUP",
  "HOME_DELIVERY",
  "NATIONWIDE",
];

export const FULFILLMENT_LABELS: Record<FulfillmentMode, string> = {
  PICKUP: "Pickup",
  HOME_DELIVERY: "Home Delivery",
  NATIONWIDE: "Nationwide",
};

/** "…can't {verb}" for the conflict sheet. */
export const FULFILLMENT_VERB: Record<FulfillmentMode, string> = {
  PICKUP: "be picked up",
  HOME_DELIVERY: "be delivered to your home",
  NATIONWIDE: "ship nationwide",
};

/** The mode label with the noun customers use ("Nationwide shipping"). */
export function fulfillmentNoun(mode: FulfillmentMode): string {
  return mode === "NATIONWIDE" ? "Nationwide shipping" : FULFILLMENT_LABELS[mode];
}

export function isValidFulfillmentMode(value: unknown): value is FulfillmentMode {
  return typeof value === "string" && (FULFILLMENT_MODE_ORDER as readonly string[]).includes(value);
}

/** First mode (in display order) a product is available for that the customer may pick. */
export function firstEligibleMode(
  productModes: ReadonlyArray<FulfillmentMode>,
  availableModes: ReadonlyArray<FulfillmentMode>,
): FulfillmentMode | null {
  return (
    FULFILLMENT_MODE_ORDER.find((m) => productModes.includes(m) && availableModes.includes(m)) ??
    null
  );
}

/** One line under the selector. Says why the mode was chosen, never where the viewer is. */
export function fulfillmentSubtitle(
  mode: FulfillmentMode,
  source: FulfillmentModeSource,
  pickupLocationCount: number,
): string {
  if (source === "LAST_ORDER") return "Based on your last order";
  if (source === "LOCATION") return "Based on your location";
  switch (mode) {
    case "PICKUP":
      return pickupLocationCount > 0
        ? `Free at ${pickupLocationCount} pickup ${pickupLocationCount === 1 ? "location" : "locations"}`
        : "Free pickup";
    case "HOME_DELIVERY":
      return "Delivered to your door";
    case "NATIONWIDE":
      return "Ships anywhere in the lower 48";
  }
}

// ---------------------------------------------------------------------------
// Hook — optimistic selection with a server-side conflict check
// ---------------------------------------------------------------------------

export type FulfillmentConflict = {
  from: FulfillmentMode;
  to: FulfillmentMode;
  items: FulfillmentConflictItem[];
};

export function useFulfillmentMode(
  fulfillment: CartFulfillment,
  options: { refetchQueries?: string[] } = {},
) {
  const { refetchQueries } = options;
  // Optimistic: the indicator slides on click and slides back if the server
  // refuses or the request fails.
  const [pendingMode, setPendingMode] = useState<FulfillmentMode | null>(null);
  const [conflict, setConflict] = useState<FulfillmentConflict | null>(null);
  const [mutate, { loading }] = useMutation(SET_FULFILLMENT_MODE_MUTATION.document(), {
    refetchQueries,
    awaitRefetchQueries: !!refetchQueries,
  });

  const serverMode = fulfillment.mode;
  const mode = pendingMode ?? serverMode;

  const commit = useCallback(
    async (to: FulfillmentMode, removeIneligible: boolean, surface: string) => {
      setPendingMode(to);
      try {
        // Dates count here: a meal whose run ends before the new mode's next
        // day (a one-week special vs. next Tuesday's ship day) can't follow.
        const res = await mutate({ variables: { mode: to, removeIneligible, checkDates: true } });
        const payload = res.data?.viewer.cart.setFulfillmentMode;
        if (payload && !payload.applied) {
          // The server found cart items that can't follow — ask before removing.
          setPendingMode(null);
          setConflict({ from: serverMode, to, items: [...payload.conflicts] });
          posthog.capture("fulfillment_conflict_prompted", {
            from_mode: serverMode,
            to_mode: to,
            item_count: payload.conflicts.length,
            surface,
          });
          return;
        }
        posthog.capture("fulfillment_mode_selected", {
          mode: to,
          previous_mode: serverMode,
          source: "EXPLICIT",
          surface,
          removed_items: payload?.conflicts.length ?? 0,
        });
      } catch (e) {
        console.error("Failed to set fulfillment mode", e);
      } finally {
        setPendingMode(null);
      }
    },
    [mutate, serverMode],
  );

  const select = useCallback(
    (to: FulfillmentMode, surface = "menu_selector") => {
      if (to === mode || loading) return;
      if (!fulfillment.availableModes.includes(to)) return;
      void commit(to, false, surface);
    },
    [mode, loading, fulfillment.availableModes, commit],
  );

  const confirmConflict = useCallback(() => {
    const c = conflict;
    setConflict(null);
    if (c) {
      posthog.capture("fulfillment_conflict_resolved", { action: "remove", to_mode: c.to });
      void commit(c.to, true, "conflict_sheet");
    }
  }, [conflict, commit]);

  const dismissConflict = useCallback(() => {
    if (conflict) {
      posthog.capture("fulfillment_conflict_resolved", { action: "keep", to_mode: conflict.to });
    }
    setConflict(null);
  }, [conflict]);

  return {
    mode,
    source: fulfillment.source,
    availableModes: fulfillment.availableModes,
    select,
    pending: loading,
    conflict,
    confirmConflict,
    dismissConflict,
  };
}
